import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  DialogActions,
  Divider,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  CalendarMonth as CalendarMonthIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  FormatListNumbered as FormatListNumberedIcon,
  GridOn as GridOnIcon,
  GridView as GridViewIcon,
  Payments as PaymentsIcon,
  PriceChange as PriceChangeIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  Scale as ScaleIcon,
  TableChart as TableChartIcon,
  ViewInAr as ViewInArIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
} from 'date-fns';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import ventasInformesService, {
  VentasResumenProductoRow,
  VentasRpt01Row,
} from '../../services/ventasInformesService';
import InformesBackButton from '../../components/shared/InformesBackButton';
import { TIPO_ARTICULO_OPTIONS, labelTipoArticulo } from '../../config/tipoArticuloOptions';

const WEEK_OPTS = { weekStartsOn: 1 as const };

/** NroTipoFormulario en BP_RPT_Ventas01 / maestro de comprobantes de venta. */
const TIPOS_COMPROBANTE_INFORME_VENTAS = [
  { value: '10', label: 'Cotización' },
  { value: '12', label: 'Pedido' },
  { value: '1', label: 'Factura' },
  { value: '2', label: 'Nota de débito' },
  { value: '3', label: 'Nota de crédito' },
] as const;

function isNotaCreditoDetalle(r: VentasRpt01Row): boolean {
  const nroTipo = String(r.nroTipoFormulario ?? '').trim();
  if (nroTipo === '3' || nroTipo === '4') return true;
  const tipU = (r.tipDescripcion ?? '').trim().toUpperCase();
  return tipU.includes('NOTA') && (tipU.includes('CRÉDITO') || tipU.includes('CREDITO'));
}

function signoVentaResumenNC(r: VentasRpt01Row): number {
  return isNotaCreditoDetalle(r) ? -1 : 1;
}

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});

const qtyFormatter = new Intl.NumberFormat('es-AR', {
  maximumFractionDigits: 0,
});

const cantidadSumFormatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const decimal3Formatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

/** Letra–punto de venta (5)–número (8), alineado a facturación y pedidos en el front. */
function formatoComprobanteDetalle(r: VentasRpt01Row): string {
  const letraRaw = (r.letra ?? '').trim();
  const sinDatos =
    !letraRaw && r.nroSuc == null && r.nroComprobante == null;
  if (sinDatos) return '—';
  const L = letraRaw || '-';
  const suc = String(r.nroSuc ?? 0).padStart(5, '0');
  const nroNum = Number(r.nroComprobante ?? 0);
  const nroStr = String(Number.isNaN(nroNum) ? 0 : nroNum).padStart(8, '0');
  return `${L}-${suc}-${nroStr}`;
}

/** Abreviaturas habituales en ventas cuando el maestro usa códigos fijos (complementa tipDescripcion). */
const VENTAS_TIPO_FORM_ABBREV: Record<string, string> = {
  '1': 'Factura',
  '3': 'ND',
  '4': 'NC',
  '9': 'REM',
  '10': 'COT',
  '11': 'AJ',
  '12': 'PED',
};

function tipoComprobanteDetalleLabel(r: VentasRpt01Row): string {
  const tip = (r.tipDescripcion ?? '').trim();
  const tipU = tip.toUpperCase();
  const forU = (r.forDescripcion ?? '').trim().toUpperCase();

  if (tipU.includes('NOTA') && (tipU.includes('CRÉDITO') || tipU.includes('CREDITO'))) return 'NC';
  if (tipU.includes('NOTA') && (tipU.includes('DÉBITO') || tipU.includes('DEBITO'))) return 'ND';
  if (tipU.includes('FACTURA') || forU.includes('FACTURA')) return 'Factura';
  if (tipU.includes('PEDIDO')) return 'PED';
  if (tipU.includes('COTIZ')) return 'COT';
  if (tipU.includes('REMITO')) return 'REM';
  if (tipU.includes('RECIBO') || forU.includes('RECIBO')) return 'REC';
  if (tipU.includes('AJUSTE') || tipU.includes('INVENTARIO')) return 'AJ';

  const nroTipo = (r.nroTipoFormulario ?? '').toString().trim();
  const fromCode = VENTAS_TIPO_FORM_ABBREV[nroTipo];
  if (fromCode) return fromCode;

  if (tip) return tip;
  const ford = (r.forDescripcion ?? '').trim();
  if (ford) return ford;
  return nroTipo || '—';
}

function fechaFmtDetalle(s?: string | null): string {
  if (!s) return '—';
  try {
    return format(new Date(s), 'dd/MM/yyyy');
  } catch {
    return '—';
  }
}

/** Etiquetas compactas en chips ramo / línea / clase; texto multilínea (sin truncar). */
const CHIP_CLASIFICACION_SX = {
  maxWidth: '100%',
  height: 'auto',
  minHeight: 22,
  alignSelf: 'flex-start',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    overflow: 'visible',
    fontSize: '0.6875rem',
    lineHeight: 1.35,
    px: '6px',
    py: '4px',
    fontWeight: 500,
    display: 'block',
  },
} as const;

/** Reparto de columnas (suma 100 %) para tabla con layout fijo sin scroll horizontal típico en Card ancho completo. */
const RESUMEN_COL = {
  producto: { width: '19%' },
  tipoArt: { width: '7%' },
  ramo: { width: '10%' },
  linea: { width: '10%' },
  clase: { width: '10%' },
  cantidad: { width: '6%' },
  monto: { width: '13%' },
  peso: { width: '9%' },
  volumen: { width: '9%' },
  accion: { width: '7%' },
} as const;

/** Períodos rápidos de fechas (alineado a contabilidad/libro-mayor). */
type QuickChipVentas =
  | 'hoy'
  | 'semana'
  | 'mes'
  | 'mesPasado'
  | 'tresMeses'
  | 'seisMeses'
  | 'anioActual';

const QUICK_CHIP_VENTAS_KEYS: QuickChipVentas[] = [
  'hoy',
  'semana',
  'mes',
  'mesPasado',
  'tresMeses',
  'seisMeses',
  'anioActual',
];

function computeQuickRangeVentas(which: QuickChipVentas): { desde: string; hasta: string } {
  const now = new Date();
  switch (which) {
    case 'hoy': {
      const d = toIsoDate(now);
      return { desde: d, hasta: d };
    }
    case 'semana':
      return {
        desde: toIsoDate(startOfWeek(now, WEEK_OPTS)),
        hasta: toIsoDate(endOfWeek(now, WEEK_OPTS)),
      };
    case 'mes':
      return {
        desde: toIsoDate(startOfMonth(now)),
        hasta: toIsoDate(endOfMonth(now)),
      };
    case 'mesPasado': {
      const ref = subMonths(now, 1);
      return {
        desde: toIsoDate(startOfMonth(ref)),
        hasta: toIsoDate(endOfMonth(ref)),
      };
    }
    case 'tresMeses':
      return {
        desde: toIsoDate(startOfDay(subMonths(now, 3))),
        hasta: toIsoDate(endOfDay(now)),
      };
    case 'seisMeses':
      return {
        desde: toIsoDate(startOfDay(subMonths(now, 6))),
        hasta: toIsoDate(endOfDay(now)),
      };
    case 'anioActual':
      return {
        desde: toIsoDate(startOfYear(now)),
        hasta: toIsoDate(endOfYear(now)),
      };
    default: {
      const _exhaustive: never = which;
      return _exhaustive;
    }
  }
}

function findMatchingQuickChipVentas(desde: string, hasta: string): QuickChipVentas | null {
  for (const key of QUICK_CHIP_VENTAS_KEYS) {
    const r = computeQuickRangeVentas(key);
    if (desde === r.desde && hasta === r.hasta) return key;
  }
  return null;
}

type KpiTarjetaProps = {
  icon: React.ReactNode;
  titulo: string;
  valor: string;
  detalle?: string;
  tooltip?: string;
  colorIcono: string;
};

const KpiTarjeta: React.FC<KpiTarjetaProps> = ({
  icon,
  titulo,
  valor,
  detalle,
  tooltip,
  colorIcono,
}) => {
  const contenido = (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            color: colorIcono,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colorIcono}14`,
            borderRadius: 1.5,
            p: 0.85,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
            {titulo}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}>
            {valor}
          </Typography>
          {detalle ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {detalle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      <Box sx={{ height: '100%' }}>{contenido}</Box>
    </Tooltip>
  ) : (
    contenido
  );
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const InformeVentasResumenProducto: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const generadoDesdeUrlRef = useRef(false);

  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = searchParams.get('fechaDesde');
    if (d && ISO_DATE_RE.test(d)) return d;
    return computeQuickRangeVentas('mes').desde;
  });
  const [fechaHasta, setFechaHasta] = useState(() => {
    const h = searchParams.get('fechaHasta');
    if (h && ISO_DATE_RE.test(h)) return h;
    return computeQuickRangeVentas('mes').hasta;
  });
  /** Vacío = todos los tipos de artículo. */
  const [tipoArticulo, setTipoArticulo] = useState('');
  const [nombreRamo, setNombreRamo] = useState('');
  const [nombreLinea, setNombreLinea] = useState('');
  const [nombreClase, setNombreClase] = useState('');
  /** Por defecto factura, ND y NC (NC resta en el resumen). */
  const [tiposComprobanteSel, setTiposComprobanteSel] = useState<string[]>(() => ['1', '2', '3']);

  const [opcionesRamos, setOpcionesRamos] = useState<string[]>([]);
  const [opcionesLineas, setOpcionesLineas] = useState<string[]>([]);
  const [opcionesClases, setOpcionesClases] = useState<string[]>([]);

  const [rows, setRows] = useState<VentasResumenProductoRow[]>([]);
  const [totales, setTotales] = useState({
    totalCantidad: 0,
    totalMonto: 0,
    totalPeso: 0,
    totalVolumen: 0,
  });
  const [cantidadComprobantesDistintos, setCantidadComprobantesDistintos] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingOpciones, setLoadingOpciones] = useState(false);

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleRows, setDetalleRows] = useState<VentasRpt01Row[]>([]);
  const [detalleTitulo, setDetalleTitulo] = useState('');
  const [detalleClave, setDetalleClave] = useState<{
    producto: string;
    tipo: string | null | undefined;
    nombreRamo: string | null | undefined;
    nombreLinea: string | null | undefined;
    nombreClase: string | null | undefined;
  } | null>(null);

  const cargarOpciones = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) return;
    try {
      setLoadingOpciones(true);
      const op = await ventasInformesService.getOpcionesResumenProducto(fechaDesde, fechaHasta);
      setOpcionesRamos(op.nombreRamos ?? []);
      setOpcionesLineas(op.nombreLineas ?? []);
      setOpcionesClases(op.nombreClases ?? []);
    } catch {
      enqueueSnackbar('No se pudieron cargar las opciones de filtro.', { variant: 'warning' });
    } finally {
      setLoadingOpciones(false);
    }
  }, [fechaDesde, fechaHasta, enqueueSnackbar]);

  useEffect(() => {
    void cargarOpciones();
  }, [cargarOpciones]);

  const presetActivo = useMemo(
    () => findMatchingQuickChipVentas(fechaDesde, fechaHasta),
    [fechaDesde, fechaHasta]
  );
  const periodoPersonalizado = Boolean(fechaDesde && fechaHasta && presetActivo === null);

  const indicadoresResumen = useMemo(() => {
    const { totalCantidad: cq, totalMonto: m, totalPeso: p, totalVolumen: v } = totales;
    const nComp = cantidadComprobantesDistintos;
    const ticketPromedio = nComp > 0 ? m / nComp : null;
    const precioMedioUnitario = Math.abs(Number(cq)) > 1e-9 ? m / Number(cq) : null;
    return { ticketPromedio, precioMedioUnitario, nComp, cq, m, p, v };
  }, [totales, cantidadComprobantesDistintos]);

  const applyQuickChip = useCallback((which: QuickChipVentas) => {
    const r = computeQuickRangeVentas(which);
    setFechaDesde(r.desde);
    setFechaHasta(r.hasta);
  }, []);

  const toggleTipoComprobante = useCallback((value: string) => {
    setTiposComprobanteSel((prev) => {
      const next = prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value];
      return next.sort((a, b) => Number(a) - Number(b));
    });
  }, []);

  const nroTiposFormularioParam = tiposComprobanteSel.join(',');

  const handleGenerar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá fecha desde y hasta.', { variant: 'warning' });
      return;
    }
    if (tiposComprobanteSel.length === 0) {
      enqueueSnackbar('Seleccioná al menos un tipo de comprobante.', { variant: 'warning' });
      return;
    }
    try {
      setLoading(true);
      const res = await ventasInformesService.getResumenProducto({
        fechaDesde,
        fechaHasta,
        tipoArticulo: tipoArticulo || undefined,
        nombreRamo: nombreRamo || undefined,
        nombreLinea: nombreLinea || undefined,
        nombreClase: nombreClase || undefined,
        nroTiposFormulario: nroTiposFormularioParam,
      });
      setRows(res.data);
      setTotales({
        totalCantidad: res.totalCantidad,
        totalMonto: res.totalMonto,
        totalPeso: res.totalPeso,
        totalVolumen: res.totalVolumen,
      });
      setCantidadComprobantesDistintos(Number(res.cantidadComprobantesDistintos ?? 0));
      enqueueSnackbar(`${res.data.length} grupo(s) cargado(s).`, { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo generar el informe.', { variant: 'error' });
      setRows([]);
      setTotales({
        totalCantidad: 0,
        totalMonto: 0,
        totalPeso: 0,
        totalVolumen: 0,
      });
      setCantidadComprobantesDistintos(0);
    } finally {
      setLoading(false);
    }
  }, [
    fechaDesde,
    fechaHasta,
    tipoArticulo,
    nombreRamo,
    nombreLinea,
    nombreClase,
    nroTiposFormularioParam,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    const d = searchParams.get('fechaDesde');
    const h = searchParams.get('fechaHasta');
    if (!d || !h || !ISO_DATE_RE.test(d) || !ISO_DATE_RE.test(h)) return;
    if (generadoDesdeUrlRef.current) return;
    generadoDesdeUrlRef.current = true;
    void handleGenerar();
  }, [searchParams, handleGenerar]);

  const exportarCsv = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const header = [
      'Producto',
      'Tipo de artículo',
      'Ramo',
      'Línea',
      'Clase',
      'Cantidad',
      'Monto Total',
      'Peso Total (kg)',
      'Volumen Total (m³)',
    ];
    const lines = [
      header.join(';'),
      ...rows.map((r) =>
        [
          `"${(r.producto ?? '').replace(/"/g, '""')}"`,
          `"${labelTipoArticulo(r.tipo).replace(/"/g, '""')}"`,
          `"${(r.nombreRamo ?? '').replace(/"/g, '""')}"`,
          `"${(r.nombreLinea ?? '').replace(/"/g, '""')}"`,
          `"${(r.nombreClase ?? '').replace(/"/g, '""')}"`,
          String(r.cantidad),
          String(r.montoTotal),
          String(r.pesoTotal),
          String(r.volumenTotal),
        ].join(';')
      ),
      [
        '"TOTAL GENERAL"',
        '',
        '',
        '',
        '',
        String(totales.totalCantidad),
        String(totales.totalMonto),
        String(totales.totalPeso),
        String(totales.totalVolumen),
      ].join(';'),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `informe-ventas-producto_${fechaDesde}_${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarExcel = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const data = rows.map((r) => ({
      Producto: r.producto,
      'Tipo de artículo': labelTipoArticulo(r.tipo),
      Ramo: r.nombreRamo ?? '',
      Línea: r.nombreLinea ?? '',
      Clase: r.nombreClase ?? '',
      Cantidad: r.cantidad,
      'Monto Total': r.montoTotal,
      'Peso Total (kg)': r.pesoTotal,
      'Volumen Total (m³)': r.volumenTotal,
    }));
    data.push({
      Producto: 'TOTAL GENERAL',
      'Tipo de artículo': '',
      Ramo: '',
      Línea: '',
      Clase: '',
      Cantidad: totales.totalCantidad,
      'Monto Total': totales.totalMonto,
      'Peso Total (kg)': totales.totalPeso,
      'Volumen Total (m³)': totales.totalVolumen,
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resumen');
    XLSX.writeFile(wb, `informe-ventas-producto_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  const handlePrint = () => {
    window.print();
  };

  const abrirDetalle = async (row: VentasResumenProductoRow) => {
    setDetalleTitulo(row.producto || '(sin nombre)');
    setDetalleClave({
      producto: row.producto,
      tipo: row.tipo,
      nombreRamo: row.nombreRamo,
      nombreLinea: row.nombreLinea,
      nombreClase: row.nombreClase,
    });
    setDetalleOpen(true);
    setDetalleLoading(true);
    setDetalleRows([]);
    try {
      const data = await ventasInformesService.postResumenProductoDetalle({
        fechaDesde,
        fechaHasta,
        producto: row.producto,
        tipo: row.tipo ?? '',
        nombreRamo: row.nombreRamo ?? '',
        nombreLinea: row.nombreLinea ?? '',
        nombreClase: row.nombreClase ?? '',
        nroTiposFormulario: nroTiposFormularioParam,
      });
      setDetalleRows(data);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo cargar el detalle.', { variant: 'error' });
    } finally {
      setDetalleLoading(false);
    }
  };

  const detalleStats = useMemo(() => {
    const rows = detalleRows;
    const sumSigned = (get: (r: VentasRpt01Row) => number) =>
      rows.reduce((acc, r) => acc + signoVentaResumenNC(r) * get(r), 0);
    const keysForm = new Set(
      rows.map((r) => `${r.nroFormulario ?? ''}|${r.nroTipoFormulario ?? ''}|${r.letra ?? ''}`)
    );
    return {
      lineas: rows.length,
      comprobantes: keysForm.size,
      cantidad: sumSigned((r) => Number(r.iteCantidad ?? 0)),
      monto: sumSigned((r) => Number(r.subtotalFinal ?? r.subtotal ?? 0)),
      peso: sumSigned((r) => Number(r.pesoTotalKg ?? 0)),
      volumen: sumSigned((r) => Number(r.volumenTotalM3 ?? 0)),
    };
  }, [detalleRows]);

  const exportarDetalleCsv = () => {
    if (detalleRows.length === 0) {
      enqueueSnackbar('No hay líneas para exportar.', { variant: 'info' });
      return;
    }
    const header = [
      'Fecha emisión',
      'Tipo',
      'Comprobante',
      'Fecha vencimiento',
      'Cliente',
      'Cantidad',
      'Precio unitario',
      'Subtotal',
      'Peso (kg)',
      'Volumen (m³)',
    ];
    const lines = [
      header.join(';'),
      ...detalleRows.map((r) =>
        [
          fechaFmtDetalle(r.fechaEmision),
          `"${tipoComprobanteDetalleLabel(r).replace(/"/g, '""')}"`,
          `"${formatoComprobanteDetalle(r).replace(/"/g, '""')}"`,
          fechaFmtDetalle(r.fechaVencim),
          `"${(r.cliNombre ?? r.nombre ?? '').replace(/"/g, '""')}"`,
          String(r.iteCantidad ?? ''),
          String(r.precioUnitarioFinal ?? r.precioUnitario ?? ''),
          String(r.subtotalFinal ?? r.subtotal ?? ''),
          String(r.pesoTotalKg ?? ''),
          String(r.volumenTotalM3 ?? ''),
        ].join(';')
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = detalleTitulo.replace(/[^\w\-]+/g, '_').slice(0, 40);
    a.download = `detalle-ventas_${safe}_${fechaDesde}_${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarDetalleExcel = () => {
    if (detalleRows.length === 0) {
      enqueueSnackbar('No hay líneas para exportar.', { variant: 'info' });
      return;
    }
    const data = detalleRows.map((r) => ({
      'Fecha emisión': fechaFmtDetalle(r.fechaEmision),
      Tipo: tipoComprobanteDetalleLabel(r),
      Comprobante: formatoComprobanteDetalle(r),
      'Fecha vencimiento': fechaFmtDetalle(r.fechaVencim),
      Cliente: r.cliNombre ?? r.nombre ?? '',
      Cantidad: r.iteCantidad ?? '',
      'Precio unitario': r.precioUnitarioFinal ?? r.precioUnitario ?? '',
      Subtotal: r.subtotalFinal ?? r.subtotal ?? '',
      'Peso (kg)': r.pesoTotalKg ?? '',
      'Volumen (m³)': r.volumenTotalM3 ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
    const safe = detalleTitulo.replace(/[^\w\-]+/g, '_').slice(0, 40);
    XLSX.writeFile(wb, `detalle-ventas_${safe}_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #informe-ventas-print-root, #informe-ventas-print-root * { visibility: visible; }
            #informe-ventas-print-root { position: absolute; left: 0; top: 0; width: 100%; padding: 16px; }
            .no-print { display: none !important; }
          }
        `}
      </style>

      <Card
        id="informe-ventas-print-root"
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              spacing={2}
              sx={{ mb: 3 }}
              className="no-print"
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <InformesBackButton to="/ventas/informes" useBrowserBack />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Informe de ventas por producto
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                    Resumen por producto, tipo de producto, ramo, línea y clase.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  loading ? <CircularProgress size={22} color="inherit" /> : <DescriptionIcon />
                }
                onClick={() => void handleGenerar()}
                disabled={loading}
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                }}
              >
                Generar Informe
              </Button>
            </Stack>

            <Box className="no-print">
              <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="informe-tipo-articulo-label">Tipo de Artículo</InputLabel>
                  <Select
                    labelId="informe-tipo-articulo-label"
                    label="Tipo de Artículo"
                    value={tipoArticulo}
                    onChange={(e) => setTipoArticulo(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos los tipos</em>
                    </MenuItem>
                    {TIPO_ARTICULO_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ramo</InputLabel>
                  <Select
                    label="Ramo"
                    value={nombreRamo}
                    onChange={(e) => setNombreRamo(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos los ramos</em>
                    </MenuItem>
                    {opcionesRamos.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Línea</InputLabel>
                  <Select
                    label="Línea"
                    value={nombreLinea}
                    onChange={(e) => setNombreLinea(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todas las líneas</em>
                    </MenuItem>
                    {opcionesLineas.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Clase</InputLabel>
                  <Select
                    label="Clase"
                    value={nombreClase}
                    onChange={(e) => setNombreClase(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todas las clases</em>
                    </MenuItem>
                    {opcionesClases.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Tipo de comprobante
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} alignItems="center">
                    {TIPOS_COMPROBANTE_INFORME_VENTAS.map((opt) => (
                      <Chip
                        key={opt.value}
                        label={opt.label}
                        size="small"
                        onClick={() => toggleTipoComprobante(opt.value)}
                        color={tiposComprobanteSel.includes(opt.value) ? 'primary' : 'default'}
                        variant={tiposComprobanteSel.includes(opt.value) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 2 }}
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Período rápido
              </Typography>
              {loadingOpciones && <CircularProgress size={18} />}
              {(
                [
                  { key: 'hoy' as const, label: 'Hoy' },
                  { key: 'semana' as const, label: 'Semana actual' },
                  { key: 'mes' as const, label: 'Mes actual' },
                  { key: 'mesPasado' as const, label: 'Mes pasado' },
                  { key: 'tresMeses' as const, label: 'Últ. 3 meses' },
                  { key: 'seisMeses' as const, label: 'Últ. 6 meses' },
                  { key: 'anioActual' as const, label: 'Año actual' },
                ] as const
              ).map(({ key, label }) => (
                <Chip
                  key={key}
                  icon={
                    <CalendarMonthIcon
                      sx={{
                        fontSize: 18,
                        color: presetActivo === key ? 'inherit' : 'primary.main',
                      }}
                    />
                  }
                  label={label}
                  size="small"
                  onClick={() => applyQuickChip(key)}
                  color={presetActivo === key ? 'primary' : 'default'}
                  variant={presetActivo === key ? 'filled' : 'outlined'}
                />
              ))}
              <Chip
                label="Personalizado"
                size="small"
                icon={
                  <CalendarMonthIcon
                    sx={{
                      fontSize: 18,
                      color: periodoPersonalizado ? 'inherit' : 'primary.main',
                    }}
                  />
                }
                color={periodoPersonalizado ? 'primary' : 'default'}
                variant={periodoPersonalizado ? 'filled' : 'outlined'}
                title="Las fechas no coinciden con un período rápido; podés editarlas arriba."
                sx={{ cursor: 'default' }}
              />
            </Stack>
            </Box>

            <Divider className="no-print" sx={{ my: 3 }} />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <TableChartIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Resultados del Informe
                </Typography>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1} className="no-print">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DescriptionIcon />}
                  onClick={exportarCsv}
                  disabled={rows.length === 0}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Exportar CSV
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<GridOnIcon />}
                  onClick={exportarExcel}
                  disabled={rows.length === 0}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Exportar Excel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={rows.length === 0}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Imprimir
                </Button>
              </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Informe agrupado por producto, tipo de artículo, nombre ramo, línea y clase.
            </Typography>

            {rows.length > 0 ? (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<AttachMoneyIcon fontSize="small" />}
                    titulo="Monto neto total"
                    valor={currencyFormatter.format(indicadoresResumen.m)}
                    tooltip="Suma de subtotales finales de línea (notas de crédito restan)."
                    colorIcono="#2e7d32"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<FormatListNumberedIcon fontSize="small" />}
                    titulo="Cantidad total"
                    valor={cantidadSumFormatter.format(indicadoresResumen.cq)}
                    tooltip="Unidades netas (notas de crédito restan cantidad)."
                    colorIcono="#1565c0"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<ReceiptLongIcon fontSize="small" />}
                    titulo="Total de comprobantes"
                    valor={indicadoresResumen.nComp.toLocaleString('es-AR')}
                    detalle=""
                    tooltip="Cantidad de comprobantes distintos en el período y filtros (por Nro. interno o sucursal / tipo / nro.)."
                    colorIcono="#6a1b9a"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<PaymentsIcon fontSize="small" />}
                    titulo="Ticket promedio"
                    valor={
                      indicadoresResumen.ticketPromedio != null
                        ? currencyFormatter.format(indicadoresResumen.ticketPromedio)
                        : '—'
                    }
                    detalle="Monto neto ÷ comprobantes"
                    tooltip="Monto neto del informe dividido por la cantidad de comprobantes distintos."
                    colorIcono="#c62828"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<PriceChangeIcon fontSize="small" />}
                    titulo="Precio medio / unidad"
                    valor={
                      indicadoresResumen.precioMedioUnitario != null
                        ? currencyFormatter.format(indicadoresResumen.precioMedioUnitario)
                        : '—'
                    }
                    detalle="Monto neto ÷ cantidad"
                    tooltip="Cociente entre monto neto total y cantidad total (promedio ponderado)."
                    colorIcono="#ef6c00"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<GridViewIcon fontSize="small" />}
                    titulo="Total de productos"
                    valor={rows.length.toLocaleString('es-AR')}
                    detalle=""
                    tooltip="Filas del informe (combinación producto, tipo, ramo, línea y clase)."
                    colorIcono="#00838f"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<ScaleIcon fontSize="small" />}
                    titulo="Peso total"
                    valor={`${decimal3Formatter.format(indicadoresResumen.p)} kg`}
                    tooltip="Peso neto total de las líneas (notas de crédito restan)."
                    colorIcono="#5d4037"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <KpiTarjeta
                    icon={<ViewInArIcon fontSize="small" />}
                    titulo="Volumen total"
                    valor={`${decimal3Formatter.format(indicadoresResumen.v)} m³`}
                    tooltip="Volumen neto total (notas de crédito restan)."
                    colorIcono="#455a64"
                  />
                </Grid>
              </Grid>
            ) : null}

            {loading && rows.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4 }}>
                Configurá filtros y pulsá «Generar Informe».
              </Typography>
            ) : (
              <TableContainer sx={{ width: '100%', overflowX: 'hidden' }}>
                <Table
                  size="small"
                  sx={{
                    width: '100%',
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': { px: 0.75, py: 0.6 },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ ...RESUMEN_COL.producto, fontSize: '0.75rem', fontWeight: 700 }}>
                        Producto
                      </TableCell>
                      <TableCell sx={{ ...RESUMEN_COL.tipoArt, fontSize: '0.7rem', fontWeight: 700 }} title="Tipo de artículo">
                        Tipo
                      </TableCell>
                      <TableCell sx={{ ...RESUMEN_COL.ramo, fontSize: '0.75rem', fontWeight: 700 }}>Ramo</TableCell>
                      <TableCell sx={{ ...RESUMEN_COL.linea, fontSize: '0.75rem', fontWeight: 700 }}>Línea</TableCell>
                      <TableCell sx={{ ...RESUMEN_COL.clase, fontSize: '0.75rem', fontWeight: 700 }}>Clase</TableCell>
                      <TableCell align="right" sx={{ ...RESUMEN_COL.cantidad, fontSize: '0.72rem', fontWeight: 700 }}>
                        Cant.
                      </TableCell>
                      <TableCell align="right" sx={{ ...RESUMEN_COL.monto, fontSize: '0.72rem', fontWeight: 700 }}>
                        Monto
                      </TableCell>
                      <TableCell align="right" sx={{ ...RESUMEN_COL.peso, fontSize: '0.72rem', fontWeight: 700 }}>
                        Peso (kg)
                      </TableCell>
                      <TableCell align="right" sx={{ ...RESUMEN_COL.volumen, fontSize: '0.72rem', fontWeight: 700 }}>
                        Vol. (m³)
                      </TableCell>
                      <TableCell
                        align="center"
                        className="no-print"
                        aria-hidden
                        sx={{ ...RESUMEN_COL.accion, width: RESUMEN_COL.accion.width, px: 0.25 }}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow
                        key={`${row.producto}-${row.tipo}-${row.nombreRamo}-${row.nombreLinea}-${row.nombreClase}-${idx}`}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                        }}
                      >
                        <TableCell
                          sx={{
                            ...RESUMEN_COL.producto,
                            verticalAlign: 'top',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            lineHeight: 1.35,
                          }}
                        >
                          {row.producto || '—'}
                        </TableCell>
                        <TableCell sx={{ ...RESUMEN_COL.tipoArt, verticalAlign: 'top', overflow: 'visible' }}>
                          {row.tipo ? (
                            <Chip
                              label={labelTipoArticulo(row.tipo)}
                              size="small"
                              sx={{
                                maxWidth: '100%',
                                bgcolor: '#fff9c4',
                                color: '#5d4037',
                                fontWeight: 500,
                                height: 'auto',
                                minHeight: 22,
                                alignSelf: 'flex-start',
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                  overflow: 'visible',
                                  fontSize: '0.68rem',
                                  px: '5px',
                                  py: '4px',
                                  display: 'block',
                                  lineHeight: 1.35,
                                },
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ ...RESUMEN_COL.ramo, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                          {row.nombreRamo ? (
                            <Chip
                              label={row.nombreRamo}
                              size="small"
                              sx={{
                                ...CHIP_CLASIFICACION_SX,
                                bgcolor: '#e3f2fd',
                                color: '#1565c0',
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ ...RESUMEN_COL.linea, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                          {row.nombreLinea ? (
                            <Chip
                              label={row.nombreLinea}
                              size="small"
                              sx={{
                                ...CHIP_CLASIFICACION_SX,
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ ...RESUMEN_COL.clase, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                          {row.nombreClase ? (
                            <Chip
                              label={row.nombreClase}
                              size="small"
                              sx={{
                                ...CHIP_CLASIFICACION_SX,
                                bgcolor: '#f3e5f5',
                                color: '#6a1b9a',
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...RESUMEN_COL.cantidad,
                            verticalAlign: 'top',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {qtyFormatter.format(row.cantidad)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...RESUMEN_COL.monto,
                            verticalAlign: 'top',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {currencyFormatter.format(row.montoTotal)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...RESUMEN_COL.peso,
                            verticalAlign: 'top',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {decimal3Formatter.format(row.pesoTotal)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...RESUMEN_COL.volumen,
                            verticalAlign: 'top',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {decimal3Formatter.format(row.volumenTotal)}
                        </TableCell>
                        <TableCell
                          align="center"
                          className="no-print"
                          sx={{ ...RESUMEN_COL.accion, px: 0.25, verticalAlign: 'top' }}
                        >
                          <IconButton
                            size="small"
                            color="info"
                            aria-label="Ver detalle"
                            onClick={() => void abrirDetalle(row)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        bgcolor: '#e3f2fd',
                        '& td': {
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          borderTop: `2px solid ${theme.palette.primary.light}`,
                        },
                      }}
                    >
                      <TableCell colSpan={5} sx={{ fontSize: '0.8rem' }}>
                        TOTAL GENERAL
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...RESUMEN_COL.cantidad, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {qtyFormatter.format(totales.totalCantidad)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...RESUMEN_COL.monto, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {currencyFormatter.format(totales.totalMonto)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...RESUMEN_COL.peso, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {decimal3Formatter.format(totales.totalPeso)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...RESUMEN_COL.volumen, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {decimal3Formatter.format(totales.totalVolumen)}
                      </TableCell>
                      <TableCell className="no-print" sx={{ ...RESUMEN_COL.accion }} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </CardContent>
      </Card>

      <Dialog
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        maxWidth="xl"
        fullWidth
        className="no-print"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            maxHeight: 'calc(100% - 48px)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pr: 1,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" component="span" fontWeight={700}>
            Detalle — {detalleTitulo}
            {detalleClave?.tipo ? ` · ${labelTipoArticulo(detalleClave.tipo)}` : ''}
            {detalleClave?.nombreRamo ? ` · ${detalleClave.nombreRamo}` : ''}
            {detalleClave?.nombreLinea ? ` · ${detalleClave.nombreLinea}` : ''}
            {detalleClave?.nombreClase ? ` · ${detalleClave.nombreClase}` : ''}
          </Typography>
          <IconButton
            aria-label="cerrar"
            size="small"
            onClick={() => setDetalleOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          {detalleLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Total líneas
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1565c0' }}>
                    {qtyFormatter.format(detalleStats.lineas)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Comprobantes
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>
                    {qtyFormatter.format(detalleStats.comprobantes)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Cantidad acumulada
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#7b1fa2' }}>
                    {cantidadSumFormatter.format(detalleStats.cantidad)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Monto total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#c62828' }}>
                    {currencyFormatter.format(detalleStats.monto)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Peso total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#616161' }}>
                    {decimal3Formatter.format(detalleStats.peso)} kg
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Volumen total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#616161' }}>
                    {decimal3Formatter.format(detalleStats.volumen)} m³
                  </Typography>
                </Grid>
              </Grid>

              {detalleRows.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  No hay líneas en el detalle.
                </Typography>
              ) : (
                <TableContainer
                  sx={{
                    maxHeight: 440,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
                        <TableCell>Fecha emisión</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Comprobante</TableCell>
                        <TableCell>Fecha vencimiento</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">Precio unitario</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                        <TableCell align="right">Peso (kg)</TableCell>
                        <TableCell align="right">Volumen (m³)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalleRows.map((r, i) => (
                        (() => {
                          const esNC = isNotaCreditoDetalle(r);
                          const colorNc = esNC ? 'error.main' : 'text.primary';
                          const cantidadSigned =
                            r.iteCantidad != null ? signoVentaResumenNC(r) * Math.abs(Number(r.iteCantidad)) : null;
                          const precioSigned =
                            r.precioUnitarioFinal != null || r.precioUnitario != null
                              ? signoVentaResumenNC(r) *
                                Math.abs(Number(r.precioUnitarioFinal ?? r.precioUnitario ?? 0))
                              : null;
                          const subtotalBase = r.subtotalFinal ?? r.subtotal;
                          const subtotalSigned =
                            subtotalBase != null ? signoVentaResumenNC(r) * Math.abs(Number(subtotalBase)) : null;
                          return (
                        <TableRow
                          key={`${r.nroFormulario}-${r.nroItem}-${i}`}
                          sx={{
                            '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                          }}
                        >
                          <TableCell>{fechaFmtDetalle(r.fechaEmision)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{tipoComprobanteDetalleLabel(r)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatoComprobanteDetalle(r)}</TableCell>
                          <TableCell>{fechaFmtDetalle(r.fechaVencim)}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{r.cliNombre ?? r.nombre ?? '—'}</TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {cantidadSigned != null ? cantidadSumFormatter.format(cantidadSigned) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {precioSigned != null ? currencyFormatter.format(precioSigned) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: colorNc, fontWeight: esNC ? 700 : 400 }}>
                            {subtotalSigned != null ? currencyFormatter.format(subtotalSigned) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {r.pesoTotalKg != null ? decimal3Formatter.format(r.pesoTotalKg) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {r.volumenTotalM3 != null ? decimal3Formatter.format(r.volumenTotalM3) : '—'}
                          </TableCell>
                        </TableRow>
                          );
                        })()
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DescriptionIcon />}
              onClick={exportarDetalleCsv}
              disabled={detalleRows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
            >
              Exportar CSV
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GridOnIcon />}
              onClick={exportarDetalleExcel}
              disabled={detalleRows.length === 0}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
            >
              Exportar Excel
            </Button>
          </Stack>
          <Button
            variant="text"
            color="primary"
            onClick={() => setDetalleOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InformeVentasResumenProducto;
